import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, FileText, Video, Headphones, BookOpen,
  Layers3, ClipboardList, Download, Link as LinkIcon, RefreshCw,
  CheckCircle2, AlertTriangle, Clock, Wand2, X,
} from "lucide-react";

const FORMAT_META = {
  script: { icon: FileText, label: "Guion + storyboard", desc: "Script narrativo JSON con secciones y shot types" },
  video: { icon: Video, label: "Video slides + narración", desc: "MP4 con moviepy · fallback storyboard_only" },
  ebook: { icon: BookOpen, label: "Ebook PDF (6 capítulos)", desc: "Long-form PDF branded DCC + aval IADA" },
  podcast: { icon: Headphones, label: "Podcast guion (+MP3 si TTS)", desc: "Guion monólogo 18-22 min con intro/outro" },
  flashcards: { icon: Layers3, label: "Flashcards Anki .apkg", desc: "24 tarjetas Q/A exportables a Anki" },
  handout: { icon: ClipboardList, label: "Handout 1 página PDF", desc: "Documento ejecutivo con bullets accionables" },
  assessment: { icon: FileText, label: "Assessment 8 preguntas", desc: "Examen JSON con explicaciones" },
};
const ALL_FORMATS = Object.keys(FORMAT_META);

export default function ContentStudio() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [programs, setPrograms] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/content-studio/generations");
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/catalog/programs", { params: { limit: 500 } })
       .then((r) => setPrograms(r.data.items || []))
       .catch(() => {});
  }, []);

  // Auto-poll while any generation is running
  const hasRunning = useMemo(
    () => items.some((it) => it.status === "running"),
    [items]
  );
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [hasRunning]);

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Fase 12 · AI Content Studio</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Genera contenido multi-formato</h1>
          <p className="mt-1 text-sm text-gray-400">
            Guion, video, ebook, podcast, flashcards, handout y assessment desde un solo prompt.
            Límite: 5 generaciones/hora. Assets adjuntables a lecciones.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="cs-refresh-btn"
                  className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white">
            <RefreshCw className="w-3 h-3" /> Refrescar
          </button>
          <button onClick={() => setShowNew(true)} data-testid="cs-new-btn"
                  className="inline-flex items-center gap-2 rounded-md bg-[#F26A21] px-4 py-2 text-sm font-semibold text-[#0B0F14] hover:brightness-110">
            <Sparkles className="w-4 h-4" /> Nueva generación
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : (
        <>
          <HpcmBatchPanel onRefresh={load} />
          {items.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="cs-empty">
              <Wand2 className="w-8 h-8 text-gray-500 mx-auto" />
              <p className="mt-3 text-sm text-gray-300">Aún no has generado contenido.</p>
              <p className="mt-1 text-xs text-gray-500">Crea la primera generación con el botón superior.</p>
            </div>
          ) : (
            <div className="grid gap-2" data-testid="cs-list">
              {items.map((it) => (
                <GenerationRow key={it.id} it={it} onOpen={() => setDetail(it)} />
              ))}
            </div>
          )}
        </>
      )}

      {showNew && (
        <NewGenerationModal
          programs={programs}
          onClose={() => setShowNew(false)}
          onCreated={(g) => {
            setShowNew(false);
            setItems((prev) => [g, ...prev]);
            setDetail(g);
          }}
        />
      )}
      {detail && (
        <DetailModal
          gid={detail.id}
          onClose={() => setDetail(null)}
          onRefresh={load}
        />
      )}
    </StaffLayout>
  );
}

function HpcmBatchPanel({ onRefresh }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [includeConsolidated, setIncludeConsolidated] = useState(true);
  const [max, setMax] = useState(4);
  const [productionMode, setProductionMode] = useState("automatic");
  const [ttsVoice, setTtsVoice] = useState("nova");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/content-studio/hpcm/status");
      setStatus(r.data);
    } catch (e) { /* silent — endpoint might not exist yet */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const queue = async () => {
    setQueuing(true);
    try {
      const r = await api.post(
        `/admin/content-studio/hpcm/generate-batch?max_items=${max}&include_consolidated=${includeConsolidated}&production_mode=${productionMode}&tts_voice=${ttsVoice}`
      );
      if (r.data.queued > 0) {
        toast.success(`${r.data.queued} generaciones encoladas. ${r.data.remaining_estimate} pendientes.`);
        onRefresh && onRefresh();
      } else {
        toast.info(r.data.message || "Nada pendiente por generar.");
      }
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail.code === "rate_limited") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setQueuing(false); }
  };

  if (!status) return null;
  const lessonsPct = status.total_lessons ? Math.round((status.lessons_with_assets / status.total_lessons) * 100) : 0;

  return (
    <div className="mb-6 rounded-2xl border border-[#F26A21]/40 bg-[#F26A21]/5 p-4" data-testid="hpcm-batch-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-bold">Curso piloto</p>
          <h3 className="mt-1 text-lg font-bold text-white">High Performance Client Management (HPCM)</h3>
          <p className="mt-1 text-xs text-gray-400">
            18 lecciones · asset kit consolidado (ebook + flashcards + 6 podcasts). Generación batch.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Cobertura de lecciones</p>
          <p className="mt-0.5 text-2xl font-bold text-white">{status.lessons_with_assets}/{status.total_lessons}</p>
          <div className="mt-1 h-1 w-32 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#F26A21] transition-all" style={{ width: `${lessonsPct}%` }} />
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center flex-wrap gap-3">
        <label className="inline-flex items-center gap-1 text-xs text-white">
          <span>Encolar:</span>
          <input type="number" min={1} max={8} value={max} onChange={(e) => setMax(Number(e.target.value))}
                 data-testid="hpcm-max-input"
                 className="w-16 rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs text-white" />
        </label>
        <select value={productionMode} onChange={(e) => setProductionMode(e.target.value)}
                data-testid="hpcm-mode-select"
                className="text-xs rounded-md bg-white/5 border border-white/10 px-2 py-1 text-white">
          <option value="automatic">Producción automática (MP4)</option>
          <option value="manual_assist">Manual assist (ZIP)</option>
        </select>
        <select value={ttsVoice} onChange={(e) => setTtsVoice(e.target.value)}
                data-testid="hpcm-voice-select"
                className="text-xs rounded-md bg-white/5 border border-white/10 px-2 py-1 text-white">
          <option value="nova">Voz Nova</option>
          <option value="onyx">Voz Onyx</option>
          <option value="alloy">Voz Alloy</option>
          <option value="fable">Voz Fable</option>
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-gray-300">
          <input type="checkbox" checked={includeConsolidated}
                 onChange={(e) => setIncludeConsolidated(e.target.checked)}
                 data-testid="hpcm-consolidated-checkbox"
                 className="accent-[#F26A21]" />
          Incluir ebook + flashcards consolidados
        </label>
        <button onClick={queue} disabled={queuing}
                data-testid="hpcm-batch-btn"
                className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#F26A21] text-[#0B0F14] text-sm font-semibold disabled:opacity-50">
          {queuing && <Loader2 className="w-3 h-3 animate-spin" />}
          <Sparkles className="w-3 h-3" /> Encolar batch
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
        <span className="rounded bg-cyan-500/20 text-cyan-200 px-1.5 py-0.5">
          Running: {status.generations_by_status?.running ?? 0}
        </span>
        <span className="rounded bg-emerald-500/20 text-emerald-200 px-1.5 py-0.5">
          Completed: {status.generations_by_status?.completed ?? 0}
        </span>
        <span className="rounded bg-red-500/20 text-red-200 px-1.5 py-0.5">
          Failed: {status.generations_by_status?.failed ?? 0}
        </span>
        <span className="rounded bg-white/10 text-gray-300 px-1.5 py-0.5">
          Total generaciones HPCM: {status.generations_total ?? 0}
        </span>
      </div>
    </div>
  );
}

function GenerationRow({ it, onOpen }) {  const total = it.formats_requested?.length || 0;
  const completed = Object.values(it.formats_completed || {}).filter((f) => f.status === "completed").length;
  const failed = Object.values(it.formats_completed || {}).filter((f) => f.status === "failed").length;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  const statusMeta = {
    running: { icon: <Loader2 className="w-3 h-3 animate-spin" />, cls: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
    completed: { icon: <CheckCircle2 className="w-3 h-3" />, cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
    failed: { icon: <AlertTriangle className="w-3 h-3" />, cls: "text-red-300 bg-red-500/10 border-red-500/30" },
  }[it.status] || { icon: <Clock className="w-3 h-3" />, cls: "text-gray-400 bg-white/5 border-white/10" };

  return (
    <button onClick={onOpen} data-testid={`cs-row-${it.id}`}
            className="text-left rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/[0.08] w-full">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{it.prompt}</p>
          <p className="mt-1 text-[11px] text-gray-500">
            {new Date(it.created_at).toLocaleString("es-MX")} · por {it.created_by_name || "—"}
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {(it.formats_requested || []).map((f) => {
              const meta = FORMAT_META[f];
              if (!meta) return null;
              const Icon = meta.icon;
              const st = (it.formats_completed || {})[f]?.status || "pending";
              const cls = st === "completed" ? "bg-emerald-500/20 text-emerald-200" :
                          st === "failed" ? "bg-red-500/20 text-red-200" :
                          st === "running" ? "bg-cyan-500/20 text-cyan-200" :
                          "bg-white/5 text-gray-400";
              return (
                <span key={f} className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${cls}`}>
                  <Icon className="w-3 h-3" /> {f}
                </span>
              );
            })}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border px-1.5 py-0.5 rounded ${statusMeta.cls}`}>
            {statusMeta.icon} {it.status}
          </span>
          <p className="mt-2 text-xs text-gray-400">{completed}/{total} listos{failed > 0 ? ` · ${failed} fallidos` : ""}</p>
          <div className="mt-1 h-1 w-32 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#F26A21] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
    </button>
  );
}

function NewGenerationModal({ programs, onClose, onCreated }) {
  const [prompt, setPrompt] = useState("");
  const [formats, setFormats] = useState(["script", "ebook", "flashcards", "handout"]);
  const [programId, setProgramId] = useState("");
  const [tone, setTone] = useState("profesional-cercano");
  const [duration, setDuration] = useState(12);
  const [productionMode, setProductionMode] = useState("automatic");
  const [ttsVoice, setTtsVoice] = useState("nova");
  const [sending, setSending] = useState(false);

  const toggleFormat = (f) => {
    setFormats((prev) => prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (prompt.trim().length < 6) return toast.error("Prompt mínimo de 6 caracteres.");
    if (formats.length === 0) return toast.error("Elige al menos 1 formato.");
    setSending(true);
    try {
      const r = await api.post("/admin/content-studio/generate", {
        prompt: prompt.trim(),
        formats,
        program_id: programId || null,
        tone, duration_target_min: Number(duration),
        language: "es-MX",
        production_mode: productionMode,
        tts_voice: ttsVoice,
      });
      toast.success("Generación iniciada.");
      onCreated(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail.code === "rate_limited") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            data-testid="cs-new-modal"
            className="w-full max-w-2xl rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white font-display inline-flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#F26A21]" /> Nueva generación
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="mt-4 space-y-4">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-1">Prompt del contenido</span>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
                      data-testid="cs-prompt-input"
                      rows={3} maxLength={2000}
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#F26A21]"
                      placeholder="Ej. Análisis de crédito estructurado para empresas medianas en México, con casos y errores comunes." required />
            <p className="text-[10px] text-gray-500 mt-1">{prompt.length}/2000. Incluye contexto sectorial para mejor calidad.</p>
          </label>

          <div>
            <p className="block text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-2">Formatos a generar</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="cs-formats-grid">
              {ALL_FORMATS.map((f) => {
                const meta = FORMAT_META[f];
                const Icon = meta.icon;
                const active = formats.includes(f);
                return (
                  <button type="button" key={f} onClick={() => toggleFormat(f)}
                          data-testid={`cs-format-${f}`}
                          className={`text-left rounded-lg border p-3 transition ${active ? "border-[#F26A21] bg-[#F26A21]/10" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${active ? "text-[#F26A21]" : "text-gray-400"}`} />
                      <span className="text-sm font-semibold text-white">{meta.label}</span>
                    </div>
                    <p className="mt-1 text-[10px] text-gray-400">{meta.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-2">
              <span className="block text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-1">Programa asociado (opcional)</span>
              <select value={programId} onChange={(e) => setProgramId(e.target.value)}
                      data-testid="cs-program-select"
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                <option value="">— Sin asociar —</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.title}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-1">Duración video (min)</span>
              <input type="number" min={3} max={30} value={duration}
                     data-testid="cs-duration-input"
                     onChange={(e) => setDuration(e.target.value)}
                     className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
            </label>
          </div>

          <label className="block">
            <span className="block text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-1">Tono</span>
            <select value={tone} onChange={(e) => setTone(e.target.value)}
                    data-testid="cs-tone-select"
                    className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
              <option value="profesional-cercano">Profesional cercano</option>
              <option value="ejecutivo-directivo">Ejecutivo · directivo</option>
              <option value="académico-riguroso">Académico · riguroso</option>
              <option value="dinamico-motivador">Dinámico · motivador</option>
            </select>
          </label>

          {formats.includes("video") && (
            <div className="rounded-lg border border-[#F26A21]/30 bg-[#F26A21]/5 p-3 space-y-3">
              <p className="text-[11px] uppercase tracking-widest text-[#F26A21] font-bold">Configuración de video</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button type="button" onClick={() => setProductionMode("automatic")}
                        data-testid="cs-mode-automatic"
                        className={`text-left rounded-lg border p-3 transition ${productionMode === "automatic" ? "border-[#F26A21] bg-[#F26A21]/10" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
                  <p className="text-sm font-semibold text-white">Producción automática</p>
                  <p className="mt-1 text-[10px] text-gray-400">
                    MP4 final: gpt-image-1 por escena + TTS `nova` + moviepy. Fallback storyboard_only si render &gt; 2 min.
                  </p>
                </button>
                <button type="button" onClick={() => setProductionMode("manual_assist")}
                        data-testid="cs-mode-manual"
                        className={`text-left rounded-lg border p-3 transition ${productionMode === "manual_assist" ? "border-[#F26A21] bg-[#F26A21]/10" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
                  <p className="text-sm font-semibold text-white">Assets para producción manual</p>
                  <p className="mt-1 text-[10px] text-gray-400">
                    ZIP con storyboard + slides + imágenes IA + narraciones MP3 + SRT. Sin render.
                  </p>
                </button>
              </div>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1">Voz de narración</span>
                <select value={ttsVoice} onChange={(e) => setTtsVoice(e.target.value)}
                        data-testid="cs-voice-select"
                        className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                  <option value="nova">Nova (femenina · joven · cálida)</option>
                  <option value="onyx">Onyx (masculina · grave · profesional)</option>
                  <option value="alloy">Alloy (neutra · balanceada)</option>
                  <option value="echo">Echo (masculina · brillante)</option>
                  <option value="fable">Fable (masculina · storytelling)</option>
                  <option value="shimmer">Shimmer (femenina · dulce)</option>
                </select>
              </label>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-white/20 text-white text-sm">Cancelar</button>
          <button type="submit" disabled={sending || prompt.trim().length < 6 || formats.length === 0}
                  data-testid="cs-submit-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            Generar
          </button>
        </div>
      </form>
    </div>
  );
}

function DetailModal({ gid, onClose }) {
  const [gen, setGen] = useState(null);
  const [attaching, setAttaching] = useState(null);
  const [lessonId, setLessonId] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/admin/content-studio/generations/${gid}`);
      setGen(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [gid]);

  // auto-refresh while running
  useEffect(() => {
    if (!gen || gen.status !== "running") return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [gen?.status]);

  const attach = async (fmt) => {
    if (!lessonId.trim()) return toast.error("Ingresa un lesson_id para adjuntar.");
    setAttaching(fmt);
    try {
      const r = await api.post(`/admin/content-studio/generations/${gid}/accept`, {
        format: fmt, add_to_lesson_id: lessonId.trim(),
      });
      toast.success(`Asset ${fmt} adjuntado a la lesson.`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setAttaching(null); }
  };

  if (!gen) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div className="text-sm text-gray-300 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           data-testid="cs-detail-modal"
           className="w-full max-w-3xl rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-bold">Generación #{gen.id.slice(0, 8)}</p>
            <h2 className="mt-1 text-xl font-bold text-white font-display">{gen.prompt}</h2>
            <p className="mt-1 text-[11px] text-gray-500">
              {new Date(gen.created_at).toLocaleString("es-MX")} · {gen.tokens_used} tokens · {(gen.latency_ms / 1000).toFixed(1)}s
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="mt-4 space-y-2" data-testid="cs-detail-formats">
          {(gen.formats_requested || []).map((fmt) => {
            const info = FORMAT_META[fmt];
            if (!info) return null;
            const Icon = info.icon;
            const data = (gen.formats_completed || {})[fmt];
            const st = data?.status || "pending";
            return (
              <div key={fmt} data-testid={`cs-fmt-row-${fmt}`}
                   className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white inline-flex items-center gap-2">
                      <Icon className="w-4 h-4 text-[#F26A21]" /> {info.label}
                    </p>
                    <p className="mt-0.5 text-[11px] text-gray-500">{info.desc}</p>
                    {data?.meta?.mocked && (
                      <p className="mt-1 text-[10px] text-amber-300 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> MOCKED · {data.meta.reason}
                      </p>
                    )}
                    {data?.meta?.video_render_mode && (
                      <p className="mt-1 text-[10px] text-cyan-300">
                        Video render: <b>{data.meta.video_render_mode}</b>
                      </p>
                    )}
                    {st === "failed" && (
                      <p className="mt-1 text-[10px] text-red-300 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {data.error}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                      st === "completed" ? "bg-emerald-500/20 text-emerald-200" :
                      st === "failed" ? "bg-red-500/20 text-red-200" :
                      st === "running" ? "bg-cyan-500/20 text-cyan-200" :
                      "bg-white/10 text-gray-400"}`}>
                      {st}
                    </span>
                    {st === "completed" && data?.url && (
                      <a href={data.url} target="_blank" rel="noreferrer"
                         data-testid={`cs-download-${fmt}`}
                         className="inline-flex items-center gap-1 text-xs text-[#F26A21] hover:brightness-125 border border-[#F26A21]/40 rounded px-2 py-1">
                        <Download className="w-3 h-3" /> Descargar
                      </a>
                    )}
                  </div>
                </div>
                {st === "completed" && data?.meta && (
                  <ExtraLinks meta={data.meta} fmt={fmt} />
                )}
              </div>
            );
          })}
        </div>

        {gen.status === "completed" && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-3" data-testid="cs-attach-panel">
            <p className="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Adjuntar a lección</p>
            <div className="mt-2 flex flex-wrap gap-2 items-end">
              <label className="block flex-1 min-w-[200px]">
                <span className="block text-[10px] text-gray-500 mb-1">lesson_id</span>
                <input value={lessonId} onChange={(e) => setLessonId(e.target.value)}
                       data-testid="cs-lesson-input"
                       className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                       placeholder="uuid de la lección" />
              </label>
              <div className="flex flex-wrap gap-1">
                {Object.entries(gen.formats_completed || {}).filter(([, d]) => d.status === "completed").map(([fmt]) => (
                  <button key={fmt} onClick={() => attach(fmt)} disabled={attaching === fmt}
                          data-testid={`cs-attach-${fmt}`}
                          className="inline-flex items-center gap-1 text-xs bg-[#B84A0C] text-white rounded px-2 py-1 hover:bg-primary">
                    {attaching === fmt ? <Loader2 className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />} {fmt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ExtraLinks({ meta, fmt }) {
  const links = [];
  if (fmt === "video") {
    if (meta.mp4_url) links.push({ label: "MP4", url: meta.mp4_url });
    if (meta.slides_url) links.push({ label: "Slides.zip", url: meta.slides_url });
    if (meta.narration_url) links.push({ label: "Narración MP3", url: meta.narration_url });
  }
  if (fmt === "podcast") {
    if (meta.script_url) links.push({ label: "Guion JSON", url: meta.script_url });
    if (meta.mp3_url) links.push({ label: "MP3", url: meta.mp3_url });
  }
  if (fmt === "flashcards") {
    if (meta.apkg_url) links.push({ label: ".apkg", url: meta.apkg_url });
    if (meta.json_url) links.push({ label: "JSON", url: meta.json_url });
  }
  if (!links.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {links.map((l) => (
        <a key={l.label} href={l.url} target="_blank" rel="noreferrer"
           className="text-[10px] rounded bg-white/5 hover:bg-white/10 px-1.5 py-0.5 text-gray-300 inline-flex items-center gap-1">
          <Download className="w-3 h-3" /> {l.label}
        </a>
      ))}
    </div>
  );
}
